import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { can } from '../Util';
import { Icon } from '../components/Utilities';

export const UrlIsActive = (url, match) => {
  if (match) {
    return window.location.pathname.match(match);
  }

  return window.location.pathname.substring(0, url.length) === url;
};

function Sidebar({
  navItems = '', userNavItems = '', baseLayoutSidebarClosed, setBaseLayoutSidebarClosed,
}) {
  const [sidebarActive, setSidebarActive] = useState(false);

  window.sidebarIsActive = sidebarActive;
  window.toggleSidebar = (newState) => {
    setSidebarActive(newState);
  };

  const handleToggleHide = () => {
    if (typeof setBaseLayoutSidebarClosed !== 'undefined') {
      setBaseLayoutSidebarClosed(!baseLayoutSidebarClosed);
    }
  };

  const getShowToggle = () => {
    if (typeof baseLayoutSidebarClosed !== 'undefined') {
      if (baseLayoutSidebarClosed) {
        return <Icon icon="arrow-right" onClick={handleToggleHide} title="Show the sidebar" />;
      }
      return <Icon icon="close" onClick={handleToggleHide} title="Temporarily hide the sidebar" />;
    }
    return null;
  };

  return (
    <div className={`sidebar ${sidebarActive ? 'active' : ''}`}>

      {getShowToggle()}

      <a className="logo" href="/">
        <img src="/img/g4m.svg" alt="Gear4music" />
      </a>

      {userNavItems}

      <div className="sidebar-content">
        <ul className="fa-ul">
          {navItems}
        </ul>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  navItems: PropTypes.node,
  userNavItems: PropTypes.node,
  baseLayoutSidebarClosed: PropTypes.bool,
  setBaseLayoutSidebarClosed: PropTypes.func,
};

Sidebar.defaultProps = {
  navItems: null,
  userNavItems: null,
  baseLayoutSidebarClosed: undefined,
  setBaseLayoutSidebarClosed: undefined,
};

export function UserNavigation({ children, activeUser, csrfToken }) {
  let active = false;

  if (children) {
    if (children.props) {
      // eslint-disable-next-line no-param-reassign
      children = [children];
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const idx in children) {
      if (children[idx].props) {
        if (UrlIsActive(children[idx].props.url, children[idx].props.match)) {
          active = true;
          break;
        }
      }
    }
  }

  const [userNavigationOpen, setUserNavigationOpen] = useState(active);
  let subNav;
  const arrow = userNavigationOpen ? 'chevron-up' : 'chevron-down';

  if (userNavigationOpen) {
    subNav = (
      <ul className="sub-nav">
        {children}

        <li>
          <form action="/logout" method="POST">
            <input type="hidden" name="_token" value={csrfToken} />
            <button type="submit">
              <i className="fa fa-fw mr-2 fa-sign-out-alt" />
              {' '}
              Logout
            </button>
          </form>
        </li>
      </ul>
    );
  }

  return (
    <div className="user">
      <button type="button" onClick={() => { setUserNavigationOpen(!userNavigationOpen); }}>
        <Icon icon={arrow} size="sm" fixed append="float-right mt-3" />
        <img alt="User profile" className="rounded-circle" src={activeUser.picture} />
        {activeUser.name}
      </button>

      {subNav}
    </div>
  );
}

UserNavigation.propTypes = {
  children: PropTypes.node,
  activeUser: PropTypes.shape({
    name: PropTypes.string.isRequired,
    picture: PropTypes.string.isRequired,
    isInGroup: PropTypes.func, // used indirectly
  }).isRequired,
  csrfToken: PropTypes.string.isRequired,
};

UserNavigation.defaultProps = {
  children: null,
};

export function NavLink({
  url, icon, label, match = false,
}) {
  const active = UrlIsActive(url, match);

  let displayIcon;
  if (icon) {
    displayIcon = <NavIcon icon={icon} />;
  }

  return (
    <li className={active ? 'active' : ''}>
      <a href={url}>
        {displayIcon}
        {label}
      </a>
    </li>
  );
}

NavLink.propTypes = {
  url: PropTypes.string.isRequired,
  icon: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  match: PropTypes.oneOfType([
    PropTypes.instanceOf(RegExp),
    PropTypes.string,
    PropTypes.bool,
  ]),
};

NavLink.defaultProps = {
  icon: undefined,
  match: false,
};

function NavIcon({ icon }) {
  return <Icon variant="fad" icon={icon} size="lg" fixed append="mr-2" />;
}

NavIcon.propTypes = {
  icon: PropTypes.string.isRequired,
};

const renderNavItem = (activeUser, item) => {
  if (item.groups) {
    const display = item.groups.reduce((val, group) => {
      if (activeUser.isInGroup(Math.abs(group)) && group > 0) {
        return true;
      }

      if (!activeUser.isInGroup(Math.abs(group)) && group < 0) {
        return true;
      }

      if (val === true) {
        return true;
      }

      return false;
    }, false);

    if (!display) {
      return '';
    }
  }

  if (item.can && !can(item.can)) {
    return '';
  }

  if (item.items) {
    // eslint-disable-next-line no-use-before-define
    return renderNavGroup(activeUser, item);
  }

  return (
    <NavLink
      key={item.url}
      url={item.url}
      icon={item.icon}
      label={item.label}
      match={item.match}
    />
  );
};

const renderNavGroup = (activeUser, item) => (
  <NavGroup key={item.label} label={item.label} icon={item.icon}>
    {item.items.map((navItem) => renderNavItem(activeUser, navItem))}
  </NavGroup>
);

export const RenderNavigation = (activeUser, nav) => renderNavItem(activeUser, nav);

export function NavGroup({ label, icon, children }) {
  let active = false;

  children.forEach((child) => {
    if (child.props && UrlIsActive(child.props.url, child.props.match)) {
      active = true;
    }
  });

  let subNav = <ul className="nav-group">{children}</ul>;
  const [isOpen, setIsOpen] = useState(active);

  if (!isOpen) {
    subNav = '';
  }

  return (
    <li className={`dropdown ${active ? 'active' : ''}`}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} href="#" className={active ? 'active' : ''}>
        <Icon variant="fal" icon={isOpen ? 'chevron-up' : 'chevron-down'} size="sm" fixed append="float-right mt-1" />
        <NavIcon icon={icon} />
        {' '}
        {label}
      </button>
      {subNav}
    </li>
  );
}

NavGroup.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  icon: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

NavGroup.defaultProps = {
  icon: undefined,
};

export default Sidebar;
